import { useMemo, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Hash,
  ChevronLeft,
  RefreshCw,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Search,
  BadgeCheck,
  Lock,
  Users,
  ShieldCheck,
  FileSpreadsheet,
  Printer,
} from "lucide-react";

import PageGate from "../../../components/PageGate";
import { PERMS } from "../../../utils/perms";
import {
  useProbationerEntrySeriesQuery,
  useProbationerMemberNumbersQuery,
  useIssueProbationerMemberNumberMutation,
  useIssueProbationerMemberNumbersForCategoryMutation,
  useFinalApproveEntryApplicationMutation,
  useFinalApproveEntryCategoryMutation,
} from "../api/probationerApplicationApi";
import {
  exportMemberNumbersPdf,
  exportMemberNumbersSpreadsheet,
} from "../utils/exportMemberNumbers";

/**
 * Entry numbers — student (S), probationer (P), technician (T).
 *
 * Two questions on one screen, because they are asked together:
 *
 *   · WHERE DOES EACH SERIES STAND — the last number handed out in each
 *     category and what the next would be. Three cards at the top, all three
 *     always shown: the series run independently and a gap in one is only
 *     visible beside the others.
 *   · WHO STILL NEEDS ONE — the diet's approved applicants, each with the
 *     number they hold or the number they would get. Numbered and unnumbered
 *     in one table, since "what number did this person get" is the same
 *     question a day later.
 *
 * Issued ONE AT A TIME and on purpose. The next free number is prefilled so
 * taking it is a single press, and the field is editable so a number already
 * written on paperwork can be typed in instead. There is no "number everyone"
 * button: these are permanent, they cannot be taken back, and a batch would
 * stamp the register faster than anyone could check it.
 */

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Where one category's series stands. */
function SeriesCard({ row }: { row: any }) {
  const s = row.series ?? {};

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
            {row.category?.name}
          </div>
          <div className="mt-1 text-2xl font-black tabular-nums text-gray-900">
            {s.highest_text ?? "—"}
          </div>
          <div className="text-[11px] font-bold text-gray-400">
            Last issued
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] font-black uppercase tracking-widest text-gray-400">
            Next
          </div>
          <div className="mt-1 text-lg font-black tabular-nums text-blue-600">
            {s.next_text ?? "—"}
          </div>
        </div>
      </div>

      {/* Where the figure came from. A "next" that surprises somebody is
          traced here rather than by reading the code: the register itself
          outranks the stored counter, and an imported spreadsheet is what
          usually moves it. */}
      <div className="mt-4 pt-3 border-t border-gray-50 text-[11px] font-semibold text-gray-400">
        {s.seeded_from === "register"
          ? "Read off the register"
          : s.seeded_from === "settings"
          ? "From the recorded counter"
          : "Nothing issued yet — starts at the configured floor"}
        {/* The shape, as set in portal settings. `next_text` above already
            carries it, but naming the parts is what lets an admin see that a
            postfix is in force rather than reading it out of the number. */}
        {s.prefix ? ` · prefix ${s.prefix}` : " · no prefix"}
        {s.postfix ? ` · postfix ${s.postfix}` : ""}
      </div>

      {!row.available ? (
        <div className="mt-3 flex items-start gap-2 rounded-xl border border-amber-100 bg-amber-50 p-2.5 text-[11px] font-bold text-amber-800">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          This series cannot be issued yet — its register column is missing on
          the server.
        </div>
      ) : null}
    </div>
  );
}

export default function AdminProbationerMemberNumbersPage() {
  const { dietId = "", categoryId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    data: seriesRows = [],
    isLoading: seriesLoading,
    refetch: refetchSeries,
  } = useProbationerEntrySeriesQuery();

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useProbationerMemberNumbersQuery(
    { dietId, categoryId },
    { skip: !dietId },
  );

  const [issue, { isLoading: issuing }] = useIssueProbationerMemberNumberMutation();
  const [issueAll, { isLoading: issuingAll }] =
    useIssueProbationerMemberNumbersForCategoryMutation();
  const [finalApprove, { isLoading: approvingOne }] =
    useFinalApproveEntryApplicationMutation();
  const [finalApproveAll, { isLoading: approvingAll }] =
    useFinalApproveEntryCategoryMutation();

  const diet = data?.diet;
  const rows: any[] = useMemo(() => data?.applications ?? [], [data]);
  const counts = data?.counts ?? {
    total: 0,
    can_issue: 0,
    can_finalize: 0,
    final_approved: 0,
    blocked: 0,
  };

  const [q, setQ] = useState("");
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null);
  /* The number being typed for one row, keyed by application id. Absent means
     "use what the server proposed" — the input shows the proposal as its
     placeholder, so an untouched field takes the next number. */
  const [edits, setEdits] = useState<Record<string, string>>({});
  /* Which row is mid-flight. `issuing` alone would grey out every button. */
  const [busyRow, setBusyRow] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      `${r.name ?? ""} ${r.email ?? ""} ${r.membership_no ?? ""} ${r.category ?? ""} ${
        r.chapter ?? ""
      }`
        .toLowerCase()
        .includes(s),
    );
  }, [rows, q]);

  const canIssue = diet?.can_issue !== false;

  /* The server says per row what may be done to it — the rules are not
     guessable from a status string, since `with_committee` covers both
     "waiting on a recommendation" and "numbered, waiting on the chairman".

     The whole-category presses are only offered when the screen IS one
     category: unscoped, the table mixes students, probationers and
     technicians, and "number everyone here" would have to mean three
     different series. */
  const awaiting = rows.filter((r) => r.can_issue);
  const toFinalize = rows.filter((r) => r.can_finalize);

  /* Named from the rows, falling back to what the screen that linked here
     handed over, so the confirmation can say which category it is about on
     the first paint rather than after the fetch lands. */
  const bulkCategory = categoryId
    ? rows[0]?.category ?? (location.state as any)?.categoryName ?? "this category"
    : null;

  /* The export is of what has actually been ISSUED — an application still
     waiting on its recommendation has no number, and a blank line in a signed
     roll of numbers is worse than its absence. Taken from the rows ON SCREEN,
     so the search box narrows it too and a listing can never disagree with the
     table it came from. */
  const issuedRows = useMemo(
    () => filtered.filter((r) => !!r.membership_no),
    [filtered],
  );

  const exportMeta = useMemo(
    () => ({
      diet: diet?.title ?? (location.state as any)?.dietTitle ?? undefined,
      category: bulkCategory ?? undefined,
      series: issuedRows[0]?.series_label ?? undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [diet?.title, location.state, bulkCategory, issuedRows],
  );

  /* Asked for, not assumed. These are permanent and cannot be taken back, and
     the whole point of the one-at-a-time design was that nobody stamps the
     register by accident — a batch needs the same deliberateness, stated. */
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [confirmApproveAll, setConfirmApproveAll] = useState(false);

  /* Final approval, one row. Creates the member profile and the joining bill;
     the button is simply gone once it has been given, which is why there is
     nothing here that reports "already approved". */
  const approveRow = async (row: any) => {
    setBanner(null);
    setBusyRow(row.id);

    try {
      const res: any = await finalApprove({ applicationId: row.id }).unwrap();
      setBanner({ ok: true, text: res?.message ?? "Final approval given." });
    } catch (e: any) {
      setBanner({
        ok: false,
        text: e?.data?.message || "That could not be approved.",
      });
    } finally {
      setBusyRow(null);
    }
  };

  const approveAll = async () => {
    setBanner(null);
    setConfirmApproveAll(false);

    try {
      const res: any = await finalApproveAll({
        dietId,
        categoryId: categoryId!,
      }).unwrap();

      setBanner({ ok: true, text: res?.message ?? "Final approval given." });
    } catch (e: any) {
      setBanner({
        ok: false,
        text: e?.data?.message || "Those could not be approved.",
      });
    }
  };

  const runBulk = async () => {
    setBanner(null);
    setConfirmBulk(false);

    try {
      const res: any = await issueAll({
        dietId,
        categoryId: categoryId!,
      }).unwrap();

      setBanner({ ok: true, text: res?.message ?? "Numbers issued." });
    } catch (e: any) {
      setBanner({
        ok: false,
        text: e?.data?.message || "Those numbers could not be issued.",
      });
    }
  };

  const assign = async (row: any) => {
    setBanner(null);
    setBusyRow(row.id);

    const typed = (edits[row.id] ?? "").trim();

    try {
      const res: any = await issue({
        applicationId: row.id,
        membership_no: typed || undefined,
      }).unwrap();

      setBanner({ ok: true, text: res?.message ?? "Number issued." });
      setEdits((e) => {
        const next = { ...e };
        delete next[row.id];
        return next;
      });
    } catch (e: any) {
      setBanner({
        ok: false,
        text: e?.data?.message || "That number could not be issued.",
      });
    } finally {
      setBusyRow(null);
    }
  };

  return (
    <PageGate perm={PERMS.application_diet.memberNumberIssue}>
      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1 text-xs font-bold text-gray-500 hover:text-gray-900"
          >
            <ChevronLeft className="w-3.5 h-3.5" /> Back
          </button>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                <Hash className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900 tracking-tight">
                  Membership numbers
                </h1>
                <p className="text-sm font-semibold text-gray-500">
                  {diet?.title ||
                    (location.state as any)?.dietTitle ||
                    "Entry applications"}
                  {" · "}
                  {counts.numbered} of {counts.approved} approved numbered
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                refetch();
                refetchSeries();
              }}
              disabled={isFetching}
              className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-500 ${isFetching ? "animate-spin" : ""}`}
              />
            </button>
          </div>

          {/* The diet rule, stated before anything is pressed rather than as a
              refusal afterwards. */}
          {!canIssue ? (
            <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50 p-4">
              <Lock className="w-5 h-5 text-amber-500 shrink-0" />
              <div className="text-sm font-semibold text-amber-800">
                This is not the active diet, so numbers cannot be issued here.
                The numbers each applicant already holds are still shown.
              </div>
            </div>
          ) : null}
        </div>

        {banner ? (
          <div
            className={`px-4 py-3 rounded-2xl text-sm font-semibold border flex items-start gap-2 ${
              banner.ok
                ? "bg-emerald-50 border-emerald-100 text-emerald-800"
                : "bg-rose-50 border-rose-100 text-rose-800"
            }`}
          >
            {banner.ok ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            )}
            {banner.text}
          </div>
        ) : null}

        {/* ── Where each series stands ─────────────────────────────────── */}
        <div>
          <h2 className="text-lg font-black text-gray-900">Series</h2>
          <p className="text-sm text-gray-500 mb-4">
            The last number issued in each category, and the next one out. Each
            category is its own running series — an S, a P and a T number with
            the same digits are three different people.
          </p>

          {seriesLoading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-40 bg-gray-50 border border-gray-100 rounded-3xl animate-pulse"
                />
              ))}
            </div>
          ) : seriesRows.length === 0 ? (
            <p className="text-sm text-gray-400">
              No category has a number series configured.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {seriesRows.map((row: any) => (
                <SeriesCard key={row.category?.id} row={row} />
              ))}
            </div>
          )}
        </div>

        {/* ── Approved applicants ──────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-black text-gray-900">
                Approved applicants
              </h2>
              <p className="text-sm text-gray-500">
                Issuing a number attaches it to the application. Final
                approval — the chairman's, or a super admin's — is what creates
                the member profile and the joining bill.
              </p>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                {counts.can_issue} awaiting a number · {counts.can_finalize} awaiting
                final approval · {counts.final_approved} member
                {counts.blocked ? ` · ${counts.blocked} not yet recommended` : ""}
              </p>
              {/* Said before the buttons are pressed: the export is of the
                  ISSUED rows on screen, so this is what will come out of it. */}
              <p className="mt-1 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                Export: {issuedRows.length} issued number
                {issuedRows.length === 1 ? "" : "s"}
                {q.trim() ? " (as filtered)" : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search name, number or chapter…"
                  className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-gray-100"
                />
              </div>

              {/* Only on a category-scoped screen — see bulkCategory. */}
              {bulkCategory && awaiting.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setConfirmBulk(true)}
                  disabled={!canIssue || issuingAll || issuing}
                  title={
                    canIssue
                      ? `Number all ${awaiting.length} approved ${bulkCategory} applicants`
                      : "Only the active diet can issue numbers"
                  }
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-40"
                >
                  {issuingAll ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Users className="w-3.5 h-3.5" />
                  )}
                  Issue all {awaiting.length}
                </button>
              ) : null}

              {/* The roll of issued numbers, as it stands on screen. Disabled
                  rather than hidden when nothing is issued: a missing export
                  button reads as "this screen cannot export", which is the
                  question somebody then asks. */}
              <button
                type="button"
                onClick={() => exportMemberNumbersSpreadsheet(issuedRows, exportMeta)}
                disabled={issuedRows.length === 0}
                title={
                  issuedRows.length === 0
                    ? "No number has been issued yet"
                    : `Download the ${issuedRows.length} issued number(s) as a spreadsheet`
                }
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-emerald-200 text-emerald-700 text-[11px] font-bold uppercase tracking-tight hover:bg-emerald-50 disabled:opacity-40"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Excel
              </button>
              <button
                type="button"
                onClick={() => exportMemberNumbersPdf(issuedRows, exportMeta)}
                disabled={issuedRows.length === 0}
                title={
                  issuedRows.length === 0
                    ? "No number has been issued yet"
                    : `Print the ${issuedRows.length} issued number(s), or save them as a PDF`
                }
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-rose-200 text-rose-700 text-[11px] font-bold uppercase tracking-tight hover:bg-rose-50 disabled:opacity-40"
              >
                <Printer className="w-4 h-4" />
                PDF
              </button>

              {/* Final approval for the whole category. Pressing it twice is
                  harmless: the second press finds nothing left to approve,
                  because approval removes the row from this count. */}
              {bulkCategory && toFinalize.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setConfirmApproveAll(true)}
                  disabled={approvingAll || approvingOne}
                  title={`Final approval for all ${toFinalize.length} issued ${bulkCategory} applications`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-indigo-700 disabled:opacity-40"
                >
                  {approvingAll ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-3.5 h-3.5" />
                  )}
                  Final approval · {toFinalize.length}
                </button>
              ) : null}
            </div>
          </div>

          {isLoading ? (
            <div className="mt-6 h-48 bg-gray-50 rounded-2xl animate-pulse" />
          ) : rows.length === 0 ? (
            <p className="mt-6 text-sm text-gray-400">
              No application in this diet has been approved yet, so there is
              nothing to number.
            </p>
          ) : filtered.length === 0 ? (
            <p className="mt-6 text-sm text-gray-400">
              Nothing matches “{q}”.
            </p>
          ) : (
            <div className="mt-4 border rounded-2xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-widest text-gray-400 border-b">
                    <th className="px-4 py-2">Applicant</th>
                    <th className="px-4 py-2">Category</th>
                    <th className="px-4 py-2">Approved</th>
                    <th className="px-4 py-2">Number</th>
                    <th className="px-4 py-2 text-right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((r) => {
                    const rowBusy = issuing && busyRow === r.id;
                    /* Why this row cannot take a number — the server's own
                       `blocked_reason` (not yet recommended by the committee
                       member) or a missing series on the server.

                       Driven off `can_issue` rather than a reason being
                       present, because those are not the same test and
                       treating them as one is what put an enabled Issue
                       button on rows the server refuses. */
                    const blockedReason =
                      r.series_missing_reason || r.blocked_reason || null;
                    const blocked = !r.can_issue;

                    return (
                      <tr key={r.id} className="align-middle">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">
                            {r.name || "—"}
                          </div>
                          <div className="text-[11px] text-gray-400">
                            {r.email || r.chapter || "—"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {r.category || "—"}
                          {r.series_label ? (
                            <div className="text-[11px] text-gray-400">
                              {r.series_label} series
                            </div>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {fmtDate(r.approved_at)}
                        </td>

                        <td className="px-4 py-3">
                          {r.numbered ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-black tabular-nums">
                              <BadgeCheck className="w-3.5 h-3.5" />
                              {r.membership_no}
                            </span>
                          ) : blocked ? (
                            <span className="text-[11px] font-semibold text-amber-700">
                              {blockedReason ?? "Not ready for a number yet."}
                            </span>
                          ) : (
                            <input
                              value={edits[r.id] ?? ""}
                              onChange={(e) =>
                                setEdits((s) => ({ ...s, [r.id]: e.target.value }))
                              }
                              placeholder={r.proposed_membership_no ?? "Number"}
                              disabled={!canIssue || rowBusy}
                              className="w-32 px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-bold tabular-nums outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50"
                            />
                          )}
                        </td>

                        {/* The row's one next step, in the order they happen:
                            issue the number, then final approval, then
                            nothing. The final-approval button is GONE once
                            given rather than disabled — an approved
                            application has no next step. */}
                        <td className="px-4 py-3 text-right">
                          {r.final_approved ? (
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-emerald-600">
                              <BadgeCheck className="w-3.5 h-3.5" />
                              Member
                            </span>
                          ) : r.can_finalize ? (
                            <button
                              type="button"
                              onClick={() => approveRow(r)}
                              disabled={approvingOne || approvingAll || rowBusy}
                              title="Final approval — creates the member profile and the joining bill"
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-[11px] font-black uppercase tracking-widest hover:bg-emerald-700 disabled:opacity-40"
                            >
                              {rowBusy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <ShieldCheck className="w-3.5 h-3.5" />
                              )}
                              Final approval
                            </button>
                          ) : r.numbered ? (
                            <span className="text-[11px] font-bold uppercase tracking-widest text-gray-300">
                              Issued
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => assign(r)}
                              disabled={!canIssue || blocked || rowBusy}
                              title={
                                canIssue
                                  ? "Attach this number to the application"
                                  : "Only the active diet can issue numbers"
                              }
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white text-[11px] font-black uppercase tracking-widest hover:bg-blue-600 disabled:opacity-40"
                            >
                              {rowBusy ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Hash className="w-3.5 h-3.5" />
                              )}
                              Issue
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {confirmApproveAll ? (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-900/60"
            role="dialog"
            aria-modal="true"
            onClick={() => setConfirmApproveAll(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b">
                <h3 className="font-bold text-gray-900">
                  Final approval for {toFinalize.length} {bulkCategory} application
                  {toFinalize.length === 1 ? "" : "s"}?
                </h3>
              </div>

              <div className="p-6 space-y-3 text-sm text-gray-600">
                <p>
                  Each becomes a member under the number already issued to
                  them. That creates their member profile and raises their
                  joining bill — the two development levies and this year's
                  dues — and opens their logbook.
                </p>
                <p className="text-xs text-gray-400">
                  Only applications that already have a number are included.
                  Pressing again later skips anyone already approved.
                </p>
              </div>

              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => setConfirmApproveAll(false)}
                  className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={approveAll}
                  disabled={approvingAll}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-50"
                >
                  {approvingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Approve {toFinalize.length}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Numbers cannot be taken back, so the batch is confirmed rather than
            fired from one press. It names the count, the category and the
            first number out, which is what makes it checkable. */}
        {confirmBulk ? (
          <div
            className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-gray-900/60"
            role="dialog"
            aria-modal="true"
            onClick={() => setConfirmBulk(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b">
                <h3 className="font-bold text-gray-900">
                  Number all {awaiting.length} {bulkCategory} applicant
                  {awaiting.length === 1 ? "" : "s"}?
                </h3>
              </div>

              <div className="p-6 space-y-3 text-sm text-gray-600">
                <p>
                  Each one gets the next number in the{" "}
                  <b>{awaiting[0]?.series_label ?? bulkCategory}</b> series,
                  starting at{" "}
                  <b className="tabular-nums">
                    {awaiting[0]?.proposed_membership_no ?? "the next free number"}
                  </b>
                  . That creates their member profile and raises their joining
                  bill — the two development levies and this year's dues.
                </p>
                <p className="font-semibold text-gray-700">
                  Numbers cannot be taken back once issued.
                </p>
                <p className="text-xs text-gray-400">
                  Anyone who cannot be numbered is skipped and named, not
                  guessed at. Pressing again later picks up whatever is left.
                </p>
              </div>

              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => setConfirmBulk(false)}
                  className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={runBulk}
                  disabled={issuingAll}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
                >
                  {issuingAll ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Issue {awaiting.length} number{awaiting.length === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <div className="p-6 bg-blue-50/50 rounded-3xl border border-blue-100 text-xs font-bold text-blue-700/70 leading-relaxed space-y-2">
          <p>
            Issuing a number creates the member's profile and raises their
            joining bill in the same step — the two development levies and the
            current year's dues, priced for their grade from the same schedule
            the January dues run uses. The message above each issue says what
            was billed, or why nothing was.
          </p>
          <p>
            A number is never reissued and never handed out twice: the register
            itself is checked before every issue, not just the stored counter,
            so a number typed in by hand or arriving on an imported spreadsheet
            cannot be given to a second person. Correcting a number already
            issued is a secretariat job on the member's own record — see{" "}
            <Link to="/settings" className="underline">
              portal settings
            </Link>{" "}
            for the prefix, postfix and width of each series.
          </p>
        </div>
      </div>
    </PageGate>
  );
}
